import { supabase } from './supabase';

// ============================================
// TYPES
// ============================================

export interface DailyInspiration {
  quote_text: string;
  author: string | null;
  source_name: string;
  background_color: string | null;
  background_image_url: string | null;
}

export interface CustomQuote {
  id: string;
  household_id: string;
  source_id: string;
  quote_text: string;
  author?: string;
  tags?: string[];
  show_on_date?: string;
  show_on_days?: number[];
  background_image_url?: string;
  background_color?: string;
  times_shown: number;
  last_shown_at?: string;
  added_by?: string;
  is_active: boolean;
  created_at: string;
}

export interface InspirationSource {
  id: string;
  household_id: string;
  name: string;
  icon: string;
  source_type: 'custom' | 'api' | 'bible' | 'preset';
  api_url?: string;
  api_config?: Record<string, unknown>;
  preset_category?: string;
  is_active: boolean;
  weight: number;
}

export type PresetCategory = 'motivational' | 'kids' | 'gratitude' | 'wisdom' | 'funny';

// ============================================
// GET DAILY INSPIRATION (via Edge Function)
// ============================================

export async function getDailyInspiration(householdId: string): Promise<DailyInspiration | null> {
  try {
    // Use direct fetch with anon key to allow unauthenticated (kiosk) access
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const response = await fetch(`${supabaseUrl}/functions/v1/get-daily-inspiration`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({ householdId, forceRefresh: false }),
    });

    if (!response.ok) {
      console.error('Edge function error:', response.status, response.statusText);
      // Fallback to database RPC
      return getDailyInspirationFallback(householdId);
    }

    const data = await response.json();

    if (data?.error) {
      console.error('Inspiration error:', data.error);
      return getDailyInspirationFallback(householdId);
    }

    return data as DailyInspiration;
  } catch (error) {
    console.error('Failed to get inspiration:', error);
    return getDailyInspirationFallback(householdId);
  }
}

// Fallback to direct database call if edge function fails
async function getDailyInspirationFallback(householdId: string): Promise<DailyInspiration | null> {
  const { data, error } = await supabase
    .rpc('get_daily_inspiration', { p_household_id: householdId });

  if (error || !data || data.length === 0) {
    console.error('Fallback RPC failed:', error);
    // Try ZenQuotes first (quote of the day), then Quotable as backup
    // Both now go through Edge Function to avoid CORS issues
    const quote = await fetchFromZenQuotes();
    if (quote) return quote;
    return fetchFromQuotable();
  }

  return data[0];
}

// Force refresh (get a new quote even if one exists for today)
export async function refreshDailyInspiration(householdId: string): Promise<DailyInspiration | null> {
  try {
    // Use direct fetch with anon key to allow unauthenticated (kiosk) access
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const response = await fetch(`${supabaseUrl}/functions/v1/get-daily-inspiration`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({ householdId, forceRefresh: true }),
    });

    if (!response.ok) {
      console.error('Refresh error:', response.status, response.statusText);
      // Fallback: delete manually and use fallback
      await supabase
        .from('daily_inspiration_log')
        .delete()
        .eq('household_id', householdId)
        .eq('shown_date', new Date().toISOString().split('T')[0]);
      return getDailyInspirationFallback(householdId);
    }

    const data = await response.json();

    if (data?.error) {
      console.error('Refresh error:', data.error);
      await supabase
        .from('daily_inspiration_log')
        .delete()
        .eq('household_id', householdId)
        .eq('shown_date', new Date().toISOString().split('T')[0]);
      return getDailyInspirationFallback(householdId);
    }

    return data as DailyInspiration;
  } catch (error) {
    console.error('Failed to refresh inspiration:', error);
    return null;
  }
}

// ============================================
// EXTERNAL QUOTE APIs (via Edge Function to avoid CORS)
// ============================================

/**
 * Fetch a quote from an external API via Edge Function
 * This avoids CORS issues by proxying through server-side
 */
async function fetchExternalQuote(
  source: 'zenquotes' | 'quotable' | 'bible',
  options?: { tags?: string[]; reference?: string }
): Promise<DailyInspiration | null> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const response = await fetch(`${supabaseUrl}/functions/v1/fetch-external-quote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({
        source,
        tags: options?.tags,
        reference: options?.reference,
      }),
    });

    if (!response.ok) {
      console.error(`External quote fetch error (${source}):`, response.status);
      return null;
    }

    const data = await response.json();

    if (data?.error) {
      console.error(`External quote API error (${source}):`, data.error);
      return null;
    }

    return data as DailyInspiration;
  } catch (error) {
    console.error(`Failed to fetch external quote (${source}):`, error);
    return null;
  }
}

// ZenQuotes API (via Edge Function)
export async function fetchFromZenQuotes(): Promise<DailyInspiration | null> {
  return fetchExternalQuote('zenquotes');
}

// Quotable API (via Edge Function)
export async function fetchFromQuotable(tags?: string[]): Promise<DailyInspiration | null> {
  return fetchExternalQuote('quotable', { tags });
}

// Bible verse (via Edge Function)
export async function fetchBibleVerse(reference?: string): Promise<DailyInspiration | null> {
  return fetchExternalQuote('bible', { reference });
}

// ============================================
// CUSTOM QUOTES MANAGEMENT
// ============================================

export async function getCustomQuotes(householdId: string): Promise<CustomQuote[]> {
  const { data, error } = await supabase
    .from('custom_quotes')
    .select(`
      *,
      added_by_member:family_members!added_by(name, avatar_emoji)
    `)
    .eq('household_id', householdId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Failed to get custom quotes:', error);
    return [];
  }

  return data || [];
}

export async function addCustomQuote(
  householdId: string,
  quoteText: string,
  options?: {
    author?: string;
    addedBy?: string;
    tags?: string[];
    showOnDate?: string;  // For birthdays, anniversaries
    backgroundColor?: string;
    backgroundImageUrl?: string;
  }
): Promise<string | null> {
  const { data, error } = await supabase
    .rpc('add_custom_quote', {
      p_household_id: householdId,
      p_quote_text: quoteText,
      p_author: options?.author || null,
      p_added_by: options?.addedBy || null,
      p_tags: options?.tags || null,
      p_show_on_date: options?.showOnDate || null,
    });

  if (error) {
    console.error('Failed to add quote:', error);
    return null;
  }

  // Update background if provided
  if (data && (options?.backgroundColor || options?.backgroundImageUrl)) {
    await supabase
      .from('custom_quotes')
      .update({
        background_color: options.backgroundColor,
        background_image_url: options.backgroundImageUrl,
      })
      .eq('id', data);
  }

  return data;
}

export async function updateCustomQuote(
  quoteId: string,
  updates: Partial<CustomQuote>
): Promise<boolean> {
  const { error } = await supabase
    .from('custom_quotes')
    .update(updates)
    .eq('id', quoteId);

  return !error;
}

export async function deleteCustomQuote(quoteId: string): Promise<boolean> {
  const { error } = await supabase
    .from('custom_quotes')
    .delete()
    .eq('id', quoteId);

  return !error;
}

// ============================================
// INSPIRATION SOURCES
// ============================================

export async function getInspirationSources(householdId: string): Promise<InspirationSource[]> {
  const { data, error } = await supabase
    .from('inspiration_sources')
    .select('*')
    .eq('household_id', householdId)
    .order('weight', { ascending: false });

  if (error) {
    console.error('Failed to get sources:', error);
    return [];
  }

  return data || [];
}

export async function createInspirationSource(
  householdId: string,
  source: Omit<InspirationSource, 'id' | 'household_id'>
): Promise<InspirationSource | null> {
  const { data, error } = await supabase
    .from('inspiration_sources')
    .insert({
      household_id: householdId,
      ...source,
    })
    .select()
    .single();

  if (error) {
    console.error('Failed to create source:', error);
    return null;
  }

  return data;
}

export async function toggleSourceActive(sourceId: string, isActive: boolean): Promise<boolean> {
  const { error } = await supabase
    .from('inspiration_sources')
    .update({ is_active: isActive })
    .eq('id', sourceId);

  return !error;
}

// ============================================
// PRESET QUOTES
// ============================================

export async function getPresetQuotes(category?: PresetCategory): Promise<{
  quote_text: string;
  author: string | null;
  category: string;
}[]> {
  let query = supabase
    .from('preset_quotes')
    .select('quote_text, author, category');

  if (category) {
    query = query.eq('category', category);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Failed to get presets:', error);
    return [];
  }

  return data || [];
}

export async function getRandomPresetQuote(category?: PresetCategory): Promise<DailyInspiration | null> {
  let query = supabase
    .from('preset_quotes')
    .select('quote_text, author, category');

  if (category) {
    query = query.eq('category', category);
  }

  const { data, error } = await query;

  if (error || !data || data.length === 0) {
    return null;
  }

  const random = data[Math.floor(Math.random() * data.length)];
  
  return {
    quote_text: random.quote_text,
    author: random.author,
    source_name: random.category,
    background_color: null,
    background_image_url: null,
  };
}

// ============================================
// INSPIRATION HISTORY
// ============================================

export async function getInspirationHistory(
  householdId: string,
  days: number = 30
): Promise<{
  shown_date: string;
  quote_text: string;
  author: string | null;
  source_name: string;
}[]> {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  const { data, error } = await supabase
    .from('daily_inspiration_log')
    .select('shown_date, quote_text, author, source_name')
    .eq('household_id', householdId)
    .gte('shown_date', startDate.toISOString().split('T')[0])
    .order('shown_date', { ascending: false });

  if (error) {
    console.error('Failed to get history:', error);
    return [];
  }

  return data || [];
}

// ============================================
// SPECIAL DATE QUOTES
// ============================================

// Add a quote for a specific date (birthday, anniversary, etc.)
export async function addSpecialDateQuote(
  householdId: string,
  date: string,  // YYYY-MM-DD
  quoteText: string,
  options?: {
    author?: string;
    addedBy?: string;
    recurAnnually?: boolean;
  }
): Promise<string | null> {
  return addCustomQuote(householdId, quoteText, {
    ...options,
    showOnDate: date,
    tags: ['special_date'],
  });
}

// Get quotes scheduled for upcoming dates
export async function getUpcomingSpecialQuotes(
  householdId: string,
  daysAhead: number = 30
): Promise<CustomQuote[]> {
  const today = new Date();
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + daysAhead);

  const { data, error } = await supabase
    .from('custom_quotes')
    .select('*')
    .eq('household_id', householdId)
    .not('show_on_date', 'is', null)
    .gte('show_on_date', today.toISOString().split('T')[0])
    .lte('show_on_date', endDate.toISOString().split('T')[0])
    .order('show_on_date');

  if (error) {
    console.error('Failed to get special quotes:', error);
    return [];
  }

  return data || [];
}
